import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { ThemeProvider, CssBaseline, Box, Typography } from "@mui/material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import {
  PermissionProvider,
  usePermissions,
} from "./contexts/PermissionContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import { theme } from "./utils/theme";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Loading } from "./components/Loading";
import ErrorBoundary from "./components/ErrorBoundary";
import { Permissions, Roles } from "./constants/permissions";
import { AppRoutes } from "./constants/appRoutes";

const Login = lazy(() =>
  import("./screens/login/Login").then((m) => ({ default: m.Login })),
);
const Signup = lazy(() =>
  import("./screens/signup/Signup").then((m) => ({ default: m.Signup })),
);
const Dashboard = lazy(() =>
  import("./screens/dashboard/Dashboard").then((m) => ({
    default: m.Dashboard,
  })),
);
const Users = lazy(() =>
  import("./screens/users/Users").then((m) => ({ default: m.Users })),
);
const RolesScreen = lazy(() =>
  import("./screens/roles/Roles").then((m) => ({ default: m.Roles })),
);
const PermissionsScreen = lazy(() =>
  import("./screens/permissions/Permissions").then((m) => ({
    default: m.Permissions,
  })),
);
const Brands = lazy(() =>
  import("./screens/brands/Brands").then((m) => ({ default: m.Brands })),
);
const IPs = lazy(() =>
  import("./screens/ips/IPs").then((m) => ({ default: m.IPs })),
);
const Tiers = lazy(() =>
  import("./screens/tiers/Tiers").then((m) => ({ default: m.Tiers })),
);
const Wallets = lazy(() =>
  import("./screens/wallets/Wallets").then((m) => ({ default: m.Wallets })),
);
const WalletHistory = lazy(() =>
  import("./screens/wallets/WalletHistory").then((m) => ({
    default: m.WalletHistory,
  })),
);
const Transactions = lazy(() =>
  import("./screens/transactions/Transactions").then((m) => ({
    default: m.Transactions,
  })),
);
const Products = lazy(() =>
  import("./screens/products/Products").then((m) => ({ default: m.Products })),
);
const Categories = lazy(() =>
  import("./screens/categories/Categories").then((m) => ({
    default: m.Categories,
  })),
);
const Coupons = lazy(() =>
  import("./screens/coupons/Coupons").then((m) => ({ default: m.Coupons })),
);
const Orders = lazy(() =>
  import("./screens/orders/Orders").then((m) => ({ default: m.Orders })),
);

const BoUsers = lazy(() =>
  import("./screens/boUsers/BoUsers").then((m) => ({ default: m.BoUsers })),
);
const Cards = lazy(() =>
  import("./screens/cards/Cards").then((m) => ({ default: m.Cards })),
);
const Profile = lazy(() =>
  import("./screens/profile/Profile").then((m) => ({ default: m.Profile })),
);
const Settings = lazy(() =>
  import("./screens/settings/Settings").then((m) => ({ default: m.Settings })),
);
const Addresses = lazy(() =>
  import("./screens/addresses/Addresses").then((m) => ({
    default: m.Addresses,
  })),
);
const CashbackRules = lazy(() =>
  import("./screens/settings/CashbackRules").then((m) => ({
    default: m.CashbackRules,
  })),
);
const RedemptionRules = lazy(
  () => import("./screens/settings/RedemptionRules"),
);
const CrossProjects = lazy(() =>
  import("./screens/crossProjects/CrossProjects").then((m) => ({
    default: m.CrossProjects,
  })),
);
const SystemHealth = lazy(() =>
  import("./screens/system/SystemHealth").then((m) => ({
    default: m.SystemHealth,
  })),
);
const BroadcastNotifications = lazy(() =>
  import("./screens/notifications/BroadcastNotifications").then((m) => ({
    default: m.BroadcastNotifications,
  })),
);
const Notifications = lazy(() =>
  import("./screens/notifications/Notifications").then((m) => ({
    default: m.Notifications,
  })),
);
const Inventory = lazy(() =>
  import("./screens/inventory/Inventory").then((m) => ({
    default: m.Inventory,
  })),
);
const AssetManager = lazy(() =>
  import("./screens/system/AssetManager").then((m) => ({
    default: m.AssetManager,
  })),
);
const AuditLogs = lazy(() =>
  import("./screens/system/AuditLogs").then((m) => ({ default: m.AuditLogs })),
);
const Logistics = lazy(() =>
  import("./screens/logistics/Logistics").then((m) => ({
    default: m.Logistics,
  })),
);
const Suppliers = lazy(() =>
  import("./screens/suppliers/Suppliers").then((m) => ({
    default: m.Suppliers,
  })),
);

const B2BCompanies = lazy(() =>
  import("./screens/b2b/Companies").then((m) => ({ default: m.Companies })),
);
const B2BDashboard = lazy(() => import("./screens/b2b/B2BDashboard"));
const B2CDashboard = lazy(() => import("./screens/dashboard/B2CDashboard"));
const B2BFinancialHealth = lazy(() =>
  import("./screens/b2b/FinancialHealthDashboard").then((m) => ({
    default: m.FinancialHealthDashboard,
  })),
);
const B2BQuotes = lazy(() =>
  import("./screens/b2b/Quotes").then((m) => ({ default: m.Quotes })),
);
const B2BOboOrder = lazy(() =>
  import("./screens/b2b/OboOrder").then((m) => ({ default: m.OboOrder })),
);
const B2BOrders = lazy(() =>
  import("./screens/orders/B2BOrders").then((m) => ({ default: m.B2BOrders })),
);
const B2BPaymentSlips = lazy(() =>
  import("./screens/b2b/PaymentSlips").then((m) => ({
    default: m.PaymentSlips,
  })),
);
const B2BBillingCenter = lazy(() =>
  import("./screens/b2b/BillingCenter").then((m) => ({
    default: m.BillingCenter,
  })),
);
const B2BAdjustmentsCenter = lazy(() =>
  import("./screens/b2b/AdjustmentsCenter").then((m) => ({
    default: m.AdjustmentsCenter,
  })),
);
const B2BDunningCenter = lazy(() =>
  import("./screens/b2b/DunningCenter").then((m) => ({
    default: m.DunningCenter,
  })),
);
const B2BFinancialReports = lazy(() =>
  import("./screens/b2b/FinancialReports").then((m) => ({
    default: m.FinancialReports,
  })),
);
const B2BConsignment = lazy(() =>
  import("./screens/b2b/Consignment").then((m) => ({ default: m.Consignment })),
);
const B2BStockTransfers = lazy(() =>
  import("./screens/b2b/StockTransfers").then((m) => ({
    default: m.StockTransfers,
  })),
);
const B2BPriceListTemplates = lazy(() =>
  import("./screens/b2b/PriceListTemplates").then((m) => ({
    default: m.PriceListTemplates,
  })),
);
const RMADesk = lazy(() => import("./screens/rma/RMADesk"));
const PreOrderDesk = lazy(() => import("./screens/preorder/PreOrderDesk"));

const AuthEventInterceptor = () => {
  const { logout } = useAuth();

  React.useEffect(() => {
    const handleAuthRequired = async () => {
      console.warn("401 Unauthorized event intercepted. Clearing local state.");
      await logout(true);
    };

    window.addEventListener("authRequired", handleAuthRequired);
    return () => window.removeEventListener("authRequired", handleAuthRequired);
  }, [logout]);

  return null;
};

const B2B_FOCUSED_ROLES = [
  Roles.B2B_ADMIN,
  Roles.FINANCE_ADMIN,
  Roles.B2B_WAREHOUSE,
];

const RootRedirect = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  if (user?.role === Roles.B2B_WAREHOUSE) {
    return <Navigate to={AppRoutes.B2B_ORDERS} replace />;
  }

  if (user?.role && B2B_FOCUSED_ROLES.includes(user.role)) {
    return <Navigate to={AppRoutes.B2B_DASHBOARD} replace />;
  }

  if (
    !hasPermission(
      [Permissions.DASHBOARD_VIEW, Permissions.DASHBOARD_ANALYTICS],
      { logic: "OR" },
    )
  ) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h5" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography color="text.secondary">
          You do not have permission to view this page.
        </Typography>
      </Box>
    );
  }

  return <Dashboard />;
};

const AppContent = () => {
  const { loading, isAuthenticated, superAdminExists } = useAuth();

  if (loading) {
    return <Loading message="Initializing..." />;
  }

  if (superAdminExists === false && !isAuthenticated) {
    return (
      <Suspense fallback={<Loading message="Loading..." />}>
        <Routes>
          <Route path={AppRoutes.SIGNUP} element={<Signup />} />
          <Route
            path="*"
            element={<Navigate to={AppRoutes.SIGNUP} replace />}
          />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<Loading message="Loading..." />}>
      <Routes>
        <Route
          path={AppRoutes.LOGIN}
          element={
            isAuthenticated ? (
              <Navigate to={AppRoutes.ROOT} replace />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path={AppRoutes.SIGNUP}
          element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />}
        />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path={AppRoutes.ROOT} element={<RootRedirect />} />
          <Route
            path={AppRoutes.NOTIFICATIONS}
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.USERS}
            element={
              <ProtectedRoute permission={Permissions.USER_READ}>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.ADDRESSES}
            element={
              <ProtectedRoute permission={Permissions.USER_READ}>
                <Addresses />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.ROLES}
            element={
              <ProtectedRoute permission={Permissions.ROLE_READ}>
                <RolesScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.PERMISSIONS}
            element={
              <ProtectedRoute permission={Permissions.PERMISSION_READ}>
                <PermissionsScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.TIERS}
            element={
              <ProtectedRoute permission={Permissions.TIER_READ}>
                <Tiers />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.WALLETS}
            element={
              <ProtectedRoute permission={Permissions.WALLET_READ}>
                <Wallets />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.WALLETS_USER_HISTORY}
            element={
              <ProtectedRoute permission={Permissions.WALLET_READ}>
                <WalletHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.TRANSACTIONS}
            element={
              <ProtectedRoute permission={Permissions.TRANSACTION_READ}>
                <Transactions />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.CARDS}
            element={
              <ProtectedRoute permission={Permissions.CARD_READ}>
                <Cards />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.BRANDS}
            element={
              <ProtectedRoute permission={Permissions.BRAND_READ}>
                <Brands />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.IPS}
            element={
              <ProtectedRoute permission={Permissions.IP_READ}>
                <IPs />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.PRODUCTS}
            element={
              <ProtectedRoute permission={Permissions.PRODUCT_READ}>
                <Products />
              </ProtectedRoute>
            }
          />

          <Route
            path={AppRoutes.INVENTORY}
            element={
              <ProtectedRoute permission={Permissions.PRODUCT_READ}>
                <Inventory />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.CATEGORIES}
            element={
              <ProtectedRoute permission={Permissions.CATEGORY_READ}>
                <Categories />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.COUPONS}
            element={
              <ProtectedRoute permission={Permissions.COUPON_READ}>
                <Coupons />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.CASHBACK_RULES}
            element={
              <ProtectedRoute permission={Permissions.SYSTEM_SETTINGS_READ}>
                <CashbackRules />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.REDEMPTION_RULES}
            element={
              <ProtectedRoute permission={Permissions.SYSTEM_SETTINGS_READ}>
                <RedemptionRules />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.ORDERS}
            element={
              <ProtectedRoute permission={Permissions.ORDER_READ}>
                <Orders />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.BO_USERS}
            element={
              <ProtectedRoute permission={Permissions.BO_USER_READ}>
                <BoUsers />
              </ProtectedRoute>
            }
          />
          <Route path={AppRoutes.PROFILE} element={<Profile />} />
          <Route
            path={AppRoutes.SETTINGS}
            element={
              <ProtectedRoute permission={Permissions.SYSTEM_SETTINGS_READ}>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.CROSS_PROJECTS}
            element={
              <ProtectedRoute permission={Permissions.SYSTEM_SETTINGS_READ}>
                <CrossProjects />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.SYSTEM_HEALTH}
            element={
              <ProtectedRoute permission={Permissions.SYSTEM_SETTINGS_READ}>
                <SystemHealth />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.AUDIT_LOGS}
            element={
              <ProtectedRoute permission={Permissions.AUDIT_LOG_READ}>
                <AuditLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.MEDIA_ASSETS}
            element={
              <ProtectedRoute permission={Permissions.CONTENT_READ}>
                <AssetManager />
              </ProtectedRoute>
            }
          />

          <Route
            path={AppRoutes.B2C_DASHBOARD}
            element={
              <ProtectedRoute
                permission={[
                  Permissions.DASHBOARD_VIEW,
                  Permissions.DASHBOARD_ANALYTICS,
                ]}
                logic="OR"
              >
                <B2CDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_DASHBOARD}
            element={
              <ProtectedRoute
                permission={[
                  Permissions.B2B_COMPANY_READ,
                  Permissions.B2B_FINANCE_READ,
                ]}
                logic="OR"
              >
                <B2BDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_FINANCIAL_HEALTH}
            element={
              <ProtectedRoute
                permission={[
                  Permissions.B2B_COMPANY_READ,
                  Permissions.B2B_FINANCE_READ,
                ]}
                logic="OR"
              >
                <B2BFinancialHealth />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_PAYMENT_SLIPS}
            element={
              <ProtectedRoute permission={Permissions.B2B_FINANCE_READ}>
                <B2BPaymentSlips />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_COMPANIES}
            element={
              <ProtectedRoute
                permission={[
                  Permissions.B2B_COMPANY_READ,
                  Permissions.B2B_FINANCE_READ,
                ]}
                logic="OR"
              >
                <B2BCompanies />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_BILLING}
            element={
              <ProtectedRoute permission={Permissions.B2B_BILLING_READ}>
                <B2BBillingCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_ADJUSTMENTS}
            element={
              <ProtectedRoute permission={Permissions.B2B_FINANCE_READ}>
                <B2BAdjustmentsCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_DUNNING}
            element={
              <ProtectedRoute permission={Permissions.B2B_FINANCE_READ}>
                <B2BDunningCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_FINANCE_REPORT}
            element={
              <ProtectedRoute permission={Permissions.B2B_FINANCE_READ}>
                <B2BFinancialReports />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_QUOTES}
            element={
              <ProtectedRoute permission={Permissions.B2B_QUOTE_READ}>
                <B2BQuotes />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_OBO}
            element={
              <ProtectedRoute permission={Permissions.B2B_ORDER_OBO}>
                <B2BOboOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_ORDERS}
            element={
              <ProtectedRoute permission={Permissions.B2B_ORDER_READ}>
                <B2BOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_LOGISTICS}
            element={
              <ProtectedRoute permission={Permissions.B2B_LOGISTICS_MANAGE}>
                <Logistics />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.SUPPLIERS}
            element={
              <ProtectedRoute permission={Permissions.SUPPLIER_READ}>
                <Suppliers />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_STOCK_TRANSFERS}
            element={
              <ProtectedRoute permission={Permissions.B2B_STOCK_TRANSFER_READ}>
                <B2BStockTransfers />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_CONSIGNMENT}
            element={
              <ProtectedRoute permission={Permissions.B2B_CONSIGNMENT_READ}>
                <B2BConsignment />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.B2B_PRICE_LIST_TEMPLATES}
            element={
              <ProtectedRoute permission={Permissions.B2B_PRICELIST_READ}>
                <B2BPriceListTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.RMA_DESK}
            element={
              <ProtectedRoute permission={Permissions.RMA_READ}>
                <RMADesk />
              </ProtectedRoute>
            }
          />
          <Route
            path={AppRoutes.PREORDERS_DESK}
            element={
              <ProtectedRoute permission={Permissions.PREORDER_READ}>
                <PreOrderDesk />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to={AppRoutes.ROOT} replace />} />
      </Routes>
    </Suspense>
  );
};

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <PermissionProvider>
              <NotificationProvider>
                <ConfirmProvider>
                  <AuthEventInterceptor />
                  <AppContent />
                </ConfirmProvider>
              </NotificationProvider>
            </PermissionProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  </React.StrictMode>,
);
